import { checkIfOpen } from './checkIfOpen';
import { getNextTimeEvent } from './getNextTimeEvent';

const DEALER_PHOTO_BASE_URL =
	'https://ddc-account-assets.ddcplatform.dealer.com/dealerphoto';

/**
 * Returns the dealer photo base URL, using the non-prod CDN host
 * when running on a non-prod (web-np) environment.
 * Uses window.location.hostname for env detection — same approach as
 * the LDP image-default widget in cms-web (LOCATION_DETAILS_V1.js).
 */
const getDealerPhotoBaseUrl = () => {
	const hostname = window?.location?.hostname || '';
	if (hostname.includes('web-np')) {
		return DEALER_PHOTO_BASE_URL.replace('ddcplatform', 'ddcplatform-np');
	}
	return DEALER_PHOTO_BASE_URL;
};

/**
 * Builds a LocationTile for each of the nearest `maxTileCount` entries of a
 * locationsResponse array, preserving the order the databus returned them in
 * (nearest first).
 *
 * @param {LocationTile[]} locationsResponse - The array of location objects to transform.
 * @param {string} locationDetailPagePart - The URL part for location detail pages.
 * @param {boolean} showAddress - Flag to determine if the address should be shown.
 * @param {number} [maxTileCount] - Maximum number of tiles to build. Expected
 * to be a positive number or omitted; callers are responsible for turning a
 * `maxTileCount` pref into one (see `useLocationsData`). Omitting it builds a
 * tile for every location.
 * @returns {LocationTileData[]}
 */
export const buildNearestLocationTiles = (
	locationsResponse,
	locationDetailPagePart,
	showAddress,
	maxTileCount
) => {
	if (!Array.isArray(locationsResponse) || locationsResponse.length === 0) {
		return [];
	}

	// Trim before building rather than after: every tile costs a pair of
	// timezone-aware format calls plus hours parsing, so there is no reason to
	// build the ones the widget will not render.
	return locationsResponse.slice(0, maxTileCount).map((location) => {
		const locationName = location?.name || '';
		const nameComaIndex = locationName.indexOf(',');
		const name =
			nameComaIndex !== -1
				? locationName.slice(0, nameComaIndex)
				: locationName;
		const now = new Date();
		const currentDay = now
			.toLocaleString('en-us', {
				weekday: 'long',
				timeZone: location?.timeZoneId
			})
			.toLowerCase();
		const currentTime = now.toLocaleTimeString('en-us', {
			hour: '2-digit',
			minute: '2-digit',
			hour12: true,
			timeZone: location?.timeZoneId
		});
		// Check if the location is open now
		const isOpen = checkIfOpen(
			location?.hours,
			currentDay,
			currentTime.replace(/\s/g, '')
		);
		const openCloseText = getNextTimeEvent(
			location?.hours,
			currentDay,
			currentTime.replace(/\s/g, '')
		);
		const href = `${locationDetailPagePart}/${encodeURIComponent(
			name.replace(/\s+/g, '-')
		)}/${encodeURIComponent(
			(location?.city || '').replace(/\s+/g, '-')
		)}.htm`;

		// Build dealer photo URL from the location's account ID.
		// Falls back to null so wsm-tiles Media component uses its
		// built-in placeholder image when no ID is available.
		const dealershipImgUrl = location?.id
			? `${getDealerPhotoBaseUrl()}/${location.id}.jpg`
			: null;

		return {
			location: {
				...location,
				address1: showAddress ? location.address1 : '',
				name,
				// truncate distance to 0 decimal places when present
				distance: Number.isFinite(location?.distance)
					? location.distance.toFixed(0)
					: null,
				isOpen,
				openCloseText,
				dealershipImgUrl,
				href
			},
			displayType: 'media'
		};
	});
};
